// src/components/PermissionsList.tsx

import React from "react";
import * as Accordion from "@radix-ui/react-accordion";
import { useTranslation } from "react-i18next";
import { permissionGroups } from "../lib/permissions";
import Checkbox from "./Checkbox";
import Icons from "@/assets/icons";

interface PermissionListProps {
  containerClassProps?: string;
  // A prop agora espera receber o novo array completo
  setValues: (values: string[]) => void;
  values: string[];
  error?: any;
}

const PermissionsList: React.FC<PermissionListProps> = ({
  containerClassProps,
  setValues,
  values,
  error,
}) => {
  const { t } = useTranslation();

  // Lógica de "Marcar Todos" ajustada
  const handleSelectAllGroup = (groupKey: string, isChecked: boolean) => {
    const group = permissionGroups.find((g) => g.key === groupKey);
    if (!group) return;

    const groupPermissionValues = group.permissions.map((p) => p.value);

    // Calcula o novo array baseado no estado atual (prop 'values')
    const newValues = isChecked
      ? [...new Set([...values, ...groupPermissionValues])] // Adiciona sem duplicar
      : values.filter((value) => !groupPermissionValues.includes(value)); // Remove

    setValues(newValues); // Envia o novo array completo
  };

  return (
    <div className={containerClassProps}>
      <Accordion.Root
        type="multiple"
        className="bg-white rounded-lg border border-neutro-200 shadow-sm"
      >
        {permissionGroups.map((group) => {
          const allGroupPermissions = group.permissions.map((p) => p.value);
          const areAllSelected = allGroupPermissions.every((p) =>
            values.includes(p)
          );

          return (
            <Accordion.Item
              key={group.key}
              value={group.key}
              className="border-b last:border-b-0 border-neutro-200"
            >
              <Accordion.Header className="flex">
                <Accordion.Trigger className="group flex flex-1 items-center justify-between p-4 font-medium text-lg hover:bg-neutro-50 transition-colors">
                  {t(`permsList.groups.${group.key}`)}
                  <Icons.ArrowDown
                    svgStyle="h-5 w-5 text-neutro-500 transition-transform duration-300 group-data-[state=open]:rotate-180"
                  />
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className="overflow-hidden data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
                <div className="p-4 pt-0">
                  <div className="mb-4 pb-4 border-b border-dashed">
                     <Checkbox
                        id={`select-all-${group.key}`}
                        label={t('permsList.actions.selectAll')}
                        checked={areAllSelected}
                        onCheckedChange={(checked: boolean) => handleSelectAllGroup(group.key, checked)}
                        labelClassProps="font-semibold text-md flex items-center cursor-pointer text-primario-500"
                     />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {group.permissions.map((perm) => (
                      <Checkbox
                        key={perm.value}
                        id={perm.value}
                        label={perm.label}
                        checked={values.includes(perm.value)}
                        // Lógica de checkbox individual ajustada
                        onCheckedChange={(checked: boolean) => {
                          const newValues = checked
                            ? [...values, perm.value]
                            : values.filter((v) => v !== perm.value);
                          setValues(newValues); // Envia o novo array completo
                        }}
                        labelClassProps="font-medium text-md flex items-center cursor-pointer"
                      />
                    ))}
                  </div>
                </div>
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
      </Accordion.Root>
      {error && typeof error === 'string' && <p className="text-erro-500 my-2">{error}</p>}
    </div>
  );
};

export default PermissionsList;